/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueProfilesPage.tsx
 ??? Created: 2025-01-27
 ?? Description:
    Queue Profiles management page with data grid, search, filtering,
    and CRUD operations for queue profiles.

 ?? Notes:
    - Lists all queue profiles
    - Supports search, filtering, and pagination
    - Provides actions: View, Edit, Duplicate, Delete
    - Uses consistent UI patterns from other modules

 ?? Last Updated: 2025-01-27 by Rodrigo Cuadra
----------------------------------------------------------------*/

import React, { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Badge } from '../../components/ui/badge';
import { type DataGridColumn } from '../../hooks/useDataGrid';
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Phone
} from 'lucide-react';
import { QueueProfile } from '../queueProfiles/types';
import { useQueueProfileList } from '../queueProfiles/hooks';
import { deleteQueueProfile } from '../queueProfiles/api';
import { navigateToQueueProfileDuplicate } from './queueProfilesActions';

export default function QueueProfilesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  // State for confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<QueueProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Available columns
  const availableColumns = useMemo<DataGridColumn<QueueProfile>[]>(() => [
    {
      key: 'name',
      label: t('queueProfiles.columns.name'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: QueueProfile) => {
        if (!item) return '-';
        return (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{item.name || '-'}</span>
          </div>
        );
      }
    },
    {
      key: 'description',
      label: t('queueProfiles.columns.description'),
      sortable: false,
      visible: true,
      render: (_: unknown, item: QueueProfile) => {
        if (!item) return '-';
        return (
          <span className="text-muted-foreground">
            {item.description || '-'}
          </span>
        );
      }
    },
    {
      key: 'enabled',
      label: t('queueProfiles.columns.status'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: QueueProfile) => {
        if (!item) return '-';
        return (
          <Badge
            variant={item.enabled ? 'default' : 'secondary'}
            size="sm"
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </Badge>
        );
      }
    },
    {
      key: 'createdAt',
      label: t('queueProfiles.columns.createdAt'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: QueueProfile) => {
        if (!item || !item.createdAt) return '-';
        return (
          <span className="text-sm text-muted-foreground">
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: t('common.actions'),
      sortable: false,
      visible: true,
    }
  ], [t]);

  // Use Queue Profile list hook
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useQueueProfileList({ initialColumns: availableColumns });

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/settings/pbx/queue-profiles/new');
  }, [navigate]);

  const handleEdit = useCallback((profile: QueueProfile) => {
    navigate(`/settings/pbx/queue-profiles/${profile.uuid ?? profile.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((profile: QueueProfile) => {
    navigateToQueueProfileDuplicate(navigate, profile.uuid ?? profile.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((profile: QueueProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      await deleteQueueProfile(profileToDelete.id);

      showSuccess(t('queueProfiles.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      refresh();
    } catch (error) {
      showError(error.message || t('queueProfiles.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, showSuccess, showError, t, refresh]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setProfileToDelete(null);
  }, []);

  // Render actions for each row
  const renderActions = useCallback((profile: QueueProfile) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(profile)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(profile)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-md transition-colors"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(profile)}
          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  ), [t, handleEdit, handleDuplicate, handleDeleteClick]);

  return (
    <div className="p-6">
      {/* Data Grid */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('queueProfiles.search.placeholder') || t('common.search')}
        title={t('queueProfiles.title')}
        titleIcon={<Phone className="h-5 w-5 text-primary" />}
        actions={
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('queueProfiles.actions.add')} position="top">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('queueProfiles.actions.add')}
              </button>
            </SimpleTooltip>
          </div>
        }
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('queueProfiles.modals.delete.title')}
        message={t('queueProfiles.modals.delete.message', { name: profileToDelete?.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

